import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Users, IndianRupee, Mail, Phone, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isAfter, isBefore, parseISO, addDays } from 'date-fns';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { calculateNights } from '../utils/date';
import { toast } from 'sonner';

const Booking = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookedDates, setBookedDates] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);

  const [formData, setFormData] = useState({
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: parseInt(searchParams.get('guests') || '2'),
    guestName: '',
    guestEmail: '',
    guestPhone: '',
  });

  /* -------------------- Scroll -------------------- */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* -------------------- Fetch Room -------------------- */
  useEffect(() => {
    fetchRoom();
  }, [roomId]);

  /* -------------------- Fetch Room Bookings for Calendar -------------------- */
  useEffect(() => {
    if (roomId) {
      fetchRoomBookings();
    }
  }, [roomId]);

  const fetchRoom = async () => {
    try {
      const res = await api.get(`/rooms/${roomId}`);
      setRoom(res.data);
    } catch {
      toast.error('Failed to load room');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomBookings = async () => {
    setLoadingAvailability(true);
    try {
      // We'll check availability for the next 6 months by checking each day
      const today = new Date();
      const sixMonthsLater = addMonths(today, 6);
      
      // Get all bookings via a simplified approach - check individual date ranges
      const bookedDatesList = [];
      
      // Fetch room bookings from a custom endpoint or check date by date
      // For efficiency, let's check availability in weekly chunks
      const startDate = startOfMonth(today);
      const endDate = endOfMonth(sixMonthsLater);
      
      // Check each day's availability
      const allDays = eachDayOfInterval({ start: startDate, end: endDate });
      
      // Batch check - check every 7 days as check-in with next day as check-out
      const checkPromises = allDays.map(async (day) => {
        const checkIn = format(day, 'yyyy-MM-dd');
        const checkOut = format(addDays(day, 1), 'yyyy-MM-dd');
        try {
          const res = await api.get(`/rooms/${roomId}/availability`, {
            params: { check_in: checkIn, check_out: checkOut },
          });
          if (!res.data.available) {
            bookedDatesList.push(day);
          }
        } catch {
          // Assume available on error
        }
      });
      
      // Process in batches to avoid overwhelming the server
      const batchSize = 30;
      for (let i = 0; i < checkPromises.length; i += batchSize) {
        const batch = checkPromises.slice(i, i + batchSize);
        await Promise.all(batch);
      }
      
      setBookedDates(bookedDatesList);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoadingAvailability(false);
    }
  };

  /* -------------------- Calendar Helpers -------------------- */
  const isDateBooked = useCallback((date) => {
    return bookedDates.some(bookedDate => isSameDay(bookedDate, date));
  }, [bookedDates]);

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isBefore(date, today);
  };

  const isDateSelectable = (date) => {
    return !isDatePast(date) && !isDateBooked(date);
  };

  const handleDateClick = (date) => {
    if (!isDateSelectable(date)) return;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    
    if (!formData.checkIn || (formData.checkIn && formData.checkOut)) {
      // Start new selection
      setFormData(prev => ({ ...prev, checkIn: dateStr, checkOut: '' }));
    } else {
      // Complete the selection
      const checkInDate = parseISO(formData.checkIn);
      if (isAfter(date, checkInDate)) {
        // Check if any dates in between are booked
        const datesInRange = eachDayOfInterval({ start: checkInDate, end: date });
        const hasBookedDateInRange = datesInRange.some(d => isDateBooked(d));
        
        if (hasBookedDateInRange) {
          toast.error('Cannot select date range with booked dates in between');
          return;
        }
        setFormData(prev => ({ ...prev, checkOut: dateStr }));
      } else {
        // Selected date is before check-in, restart
        setFormData(prev => ({ ...prev, checkIn: dateStr, checkOut: '' }));
      }
    }
  };

  const isDateInRange = (date) => {
    if (!formData.checkIn || !formData.checkOut) return false;
    const checkIn = parseISO(formData.checkIn);
    const checkOut = parseISO(formData.checkOut);
    return isAfter(date, checkIn) && isBefore(date, checkOut);
  };

  const isCheckIn = (date) => {
    if (!formData.checkIn) return false;
    return isSameDay(date, parseISO(formData.checkIn));
  };

  const isCheckOut = (date) => {
    if (!formData.checkOut) return false;
    return isSameDay(date, parseISO(formData.checkOut));
  };

  /* -------------------- Availability Check -------------------- */
  const checkAvailability = async () => {
    const res = await api.get(`/rooms/${roomId}/availability`, {
      params: {
        check_in: formData.checkIn,
        check_out: formData.checkOut,
      },
    });
    return res.data.available;
  };

  /* -------------------- Razorpay Loader -------------------- */
  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.checkIn || !formData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    if (!formData.guestName || !formData.guestEmail || !formData.guestPhone) {
      toast.error('Please fill in all guest details');
      return;
    }

    setSubmitting(true);

    try {
      const stillAvailable = await checkAvailability();
      if (!stillAvailable) {
        toast.error('This room is sold out for the selected dates');
        setSubmitting(false);
        return;
      }

      const bookingRes = await api.post('/bookings', {
        room_id: roomId,
        guest_name: formData.guestName,
        guest_email: formData.guestEmail,
        guest_phone: formData.guestPhone,
        check_in: new Date(formData.checkIn).toISOString(),
        check_out: new Date(formData.checkOut).toISOString(),
        num_guests: formData.guests,
      });

      const booking = bookingRes.data;

      const paymentRes = await api.post('/payments/create-order', {
        booking_id: booking.id,
        amount: booking.total_amount,
        payment_method: 'razorpay',
      });

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway');
        setSubmitting(false);
        return;
      }

      const razorpay = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        order_id: paymentRes.data.razorpay_order_id,
        amount: paymentRes.data.amount * 100,
        currency: 'INR',
        name: 'The BaghHome',
        description: `Booking for ${room.name}`,
        handler: async (response) => {
          try {
            await api.post('/payments/verify-razorpay', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking.id,
            });

            toast.success('Payment successful!');
            navigate(`/confirmation/${booking.id}`);
          } catch {
            toast.error('Payment verification failed');
            setSubmitting(false);
          }
        },
        prefill: {
          name: formData.guestName,
          email: formData.guestEmail,
          contact: formData.guestPhone,
        },
        theme: { color: '#1A3C34' },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled');
            setSubmitting(false);
          },
        },
      });

      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
      setSubmitting(false);
    }
  };

  /* -------------------- Calculations -------------------- */
  const nights =
    formData.checkIn && formData.checkOut
      ? calculateNights(formData.checkIn, formData.checkOut)
      : 0;

  const totalAmount = room ? nights * room.price_per_night : 0;

  /* -------------------- Calendar Render -------------------- */
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Get the day of week for the first day (0 = Sunday)
    const startDay = monthStart.getDay();
    
    // Create empty cells for days before the month starts
    const emptyCells = Array.from({ length: startDay }, (_, i) => (
      <div key={`empty-${i}`} className="h-10 w-10" />
    ));

    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => setCurrentMonth(prev => addMonths(prev, -1))}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft size={20} style={{ color: '#1A3C34' }} />
          </button>
          <h3 className="text-lg font-semibold" style={{ color: '#1A3C34' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <button
            type="button"
            onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRight size={20} style={{ color: '#1A3C34' }} />
          </button>
        </div>
        
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {emptyCells}
          {days.map(day => {
            const isBooked = isDateBooked(day);
            const isPast = isDatePast(day);
            const isSelected = isCheckIn(day) || isCheckOut(day);
            const isInRange = isDateInRange(day);
            const selectable = isDateSelectable(day);
            
            let bgColor = 'bg-white hover:bg-gray-50';
            let textColor = 'text-gray-900';
            let cursor = 'cursor-pointer';
            
            if (isPast) {
              bgColor = 'bg-gray-100';
              textColor = 'text-gray-400';
              cursor = 'cursor-not-allowed';
            } else if (isBooked) {
              bgColor = 'bg-red-100';
              textColor = 'text-red-600';
              cursor = 'cursor-not-allowed';
            } else if (isSelected) {
              bgColor = 'bg-[#1A3C34]';
              textColor = 'text-white';
            } else if (isInRange) {
              bgColor = 'bg-[#8F9E8B]/30';
              textColor = 'text-[#1A3C34]';
            } else {
              bgColor = 'bg-green-50 hover:bg-green-100';
              textColor = 'text-green-700';
            }
            
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleDateClick(day)}
                disabled={!selectable}
                className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${bgColor} ${textColor} ${cursor}`}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-50 border border-green-200" />
            <span style={{ color: '#64748B' }}>Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-100 border border-red-200" />
            <span style={{ color: '#64748B' }}>Booked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-[#1A3C34]" />
            <span style={{ color: '#64748B' }}>Selected</span>
          </div>
        </div>
        
        {loadingAvailability && (
          <p className="text-center text-sm mt-4" style={{ color: '#64748B' }}>
            Loading availability...
          </p>
        )}
      </div>
    );
  };

  /* -------------------- UI -------------------- */
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  if (!room) {
    return <div className="min-h-screen flex items-center justify-center">Room not found</div>;
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      <div className="pt-32 pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1
              className="text-4xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Book {room.name}
            </h1>
            <p style={{ color: '#64748B' }}>{room.description}</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#1A3C34' }}>
                Select Your Dates
              </h2>
              {renderCalendar()}
              
              {/* Selected dates summary */}
              {(formData.checkIn || formData.checkOut) && (
                <div className="mt-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm" style={{ color: '#64748B' }}>Check-in</p>
                      <p className="font-semibold" style={{ color: '#1A3C34' }}>
                        {formData.checkIn || 'Select date'}
                      </p>
                    </div>
                    <div className="text-2xl" style={{ color: '#8F9E8B' }}>→</div>
                    <div>
                      <p className="text-sm" style={{ color: '#64748B' }}>Check-out</p>
                      <p className="font-semibold" style={{ color: '#1A3C34' }}>
                        {formData.checkOut || 'Select date'}
                      </p>
                    </div>
                  </div>
                  {nights > 0 && (
                    <p className="text-center mt-2 text-sm" style={{ color: '#8F9E8B' }}>
                      {nights} night{nights > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </motion.div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-sm">
                <h2 className="text-xl font-semibold mb-6" style={{ color: '#1A3C34' }}>
                  Guest Details
                </h2>
                
                {/* Guest Name */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#1A3C34' }}>
                    <User size={18} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.guestName}
                    onChange={(e) => setFormData(prev => ({ ...prev, guestName: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1A3C34]/20 focus:border-[#1A3C34]"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                
                {/* Guest Email */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#1A3C34' }}>
                    <Mail size={18} />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.guestEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, guestEmail: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1A3C34]/20 focus:border-[#1A3C34]"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                
                {/* Guest Phone */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#1A3C34' }}>
                    <Phone size={18} />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.guestPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, guestPhone: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1A3C34]/20 focus:border-[#1A3C34]"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
                
                {/* Guests */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#1A3C34' }}>
                    <Users size={18} />
                    Number of Guests
                  </label>
                  <select
                    value={formData.guests}
                    onChange={(e) => setFormData(prev => ({ ...prev, guests: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1A3C34]/20 focus:border-[#1A3C34]"
                  >
                    {Array.from({ length: room.max_guests }, (_, i) => i + 1).map(num => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'Guest' : 'Guests'}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Price Summary */}
                <div className="p-4 rounded-xl mb-6" style={{ background: '#F9F8F6' }}>
                  <div className="flex justify-between items-center mb-2">
                    <span style={{ color: '#64748B' }}>₹{room.price_per_night} × {nights} night{nights !== 1 ? 's' : ''}</span>
                    <span className="font-medium" style={{ color: '#1A3C34' }}>₹{totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between items-center">
                    <span className="font-semibold" style={{ color: '#1A3C34' }}>Total</span>
                    <span className="text-xl font-bold" style={{ color: '#1A3C34' }}>₹{totalAmount.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !formData.checkIn || !formData.checkOut}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing…' : 'Proceed to Payment'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
